import {
  ItemTitle,
  ScoreChart,
  SubsectionTitle,
} from '@sleepstation/components'
import { VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import { LAYOUT_SPACING } from '@sleepstation/const'
import { parseHtmlToAst } from '@sleepstation/markdown'
import { marginH, themeFontBody } from '@sleepstation/style-api-helpers'
import { replaceTokens } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { toLower } from 'ramda'
import React from 'react'
import styled from 'styled-components'
import { edgesNodesToList } from '../../../../../../../utils/graphql'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const SUBTITLE_TEMPLATE = `Your #{name} score is #{score}`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const formatTitle = toLower
const formatScore = ({ score, total }) => `${score}/${total}`

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.LARGE,
})``

const Header = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.MEDIUM,
})``

const Body = styled.div`
  ${marginH([0.5, 0.5, 0.75])}
`

const Title = styled(SubsectionTitle).attrs({
  'data-cy': `titled-score-chart-title`,
})`
  ${themeFontBody(`normal`)}
`

const Subtitle = styled(ItemTitle).attrs({
  'data-cy': `titled-score-chart-subtitle`,
})`
  ${themeFontBody(`normal`)}
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const TitledScoreChart = ({
  description,
  colors,
  labels,
  boundaries,
  title,
  score,
  total,
  dataCy,
}) => {
  const hast = parseHtmlToAst(description)
  const flattenedBoundaries = edgesNodesToList(boundaries)
  const subtitle = replaceTokens(SUBTITLE_TEMPLATE, {
    name: formatTitle(title),
    score: formatScore({ score, total }),
  })

  return (
    <Layout data-cy={dataCy}>
      <Header>
        <Title>
          <CopyRenderer htmlAst={hast} />
        </Title>
        <Subtitle>{subtitle}</Subtitle>
      </Header>
      <Body>
        <ScoreChart
          score={score}
          total={total}
          boundaries={flattenedBoundaries}
          colors={colors}
          labels={labels}
          scoreFormatter={formatScore}
        />
      </Body>
    </Layout>
  )
}

TitledScoreChart.propTypes = {
  description: PropTypes.string.isRequired,
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  boundaries: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  dataCy: PropTypes.string.isRequired,
}

export default TitledScoreChart
